/**
 * Converts a date string in the format "dd/MM/yyyy" or "dd-MM-yyyy" to ISO 8601 format ("yyyy-MM-dd").
 *
 * @param {string} dateStr - The date string to format.
 * @returns {string} The formatted date string in ISO 8601 format ("yyyy-MM-dd").
 * @throws {Error} If the date format is not supported.
 */
export const formatDateToISO = (dateStr) => {
  let day, month, year;

  if (dateStr.includes('/')) {
    // Format is dd/MM/yyyy
    [day, month, year] = dateStr.split('/').map(Number);
  } else if (dateStr.includes('-')) {
    // Format is dd-MM-yyyy
    [day, month, year] = dateStr.split('-').map(Number);
  } else {
    throw new Error('Unsupported date format');
  }

  // Ensure day, month, and year are in proper format and pad with zeros if needed
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * Converts a date-time string in the format "dd-MM-yyyy hh:mm:ss" to ISO 8601 format ("yyyy-MM-ddTHH:mm:ss").
 *
 * @param {string} dateTimeStr - The date-time string to format.
 * @returns {string} The formatted date-time string in ISO 8601 format ("yyyy-MM-ddTHH:mm:ss").
 * @throws {Error} If the date-time format is invalid.
 */
export const formatDateTimeToISO = (dateTimeStr) => {
  // Check for the presence of the space separating the date and time
  const [datePart, timePart] = dateTimeStr.split(' ');

  if (!datePart || !timePart) {
    throw new Error('Invalid date-time format');
  }

  // Split date part into day, month, year
  const [day, month, year] = datePart.split('-').map(Number);

  // Split time part into hours, minutes, seconds
  const [hours, minutes, seconds] = timePart.split(':').map(Number);

  // Convert to ISO 8601 format
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Formats the columns of a row based on their data types.
 *
 * @param {Object[]} rows - The array of row objects to format.
 * @param {Object[]} columnDefinitions - The column definitions specifying data types.
 * @param {string} [dataKey='accessorKey'] - The key used to access values in the row (default is 'accessorKey').
 * @param {string} [dataTypeField='dataType'] - The key used to access the data type of each column (default is 'dataType').
 * @returns {Object[]} The array of formatted rows.
 */
export const formatColumnsByDataType = (
  rows,
  columnDefinitions,
  dataKey = 'accessorKey',
  dataTypeField = 'dataType',
) => {
  try {
    return rows.map((row) => {
      const updatedRow = { ...row };

      columnDefinitions.forEach((column) => {
        const key = column[dataKey]; // The key used to access values in row
        const dataType = column[dataTypeField]; // The data type for the current column

        const value = row[key]; // Get the value from the row using the key

        if (value === undefined || value === null) {
          updatedRow[key] = null;
          return;
        }

        switch (dataType) {
          case 'bigint':
            updatedRow[key] = parseInt(value, 16); // Convert to integer
            break;
          case 'int':
            updatedRow[key] = parseInt(value, 10); // Convert to integer
            break;
          case 'datetime':
            updatedRow[key] = value ? formatDateTimeToISO(value) : null;
            break;
          case 'date':
            updatedRow[key] = value ? formatDateToISO(value) : null;
            break;
          case 'boolean':
            updatedRow[key] = !!value; // Convert to boolean
            break;
          case 'text':
          default:
            updatedRow[key] = `${value}`; // Convert to string
            break;
        }
      });

      return updatedRow;
    });
  } catch (err) {
    console.log('ERROR FORMATTING IN formatColumnsByDataType()');
    console.log(err);
  }
};

export const formatToPortugalCurrency = new Intl.NumberFormat('pt-PT', {
  style: 'currency',
  currency: 'EUR',
});

export const formatToPortugalNumeric = new Intl.NumberFormat('pt-PT');
